import { Card, Title, Metric, Text, Flex, Grid } from '@tremor/react';
import { Tooltip } from './Tooltip';
import type { QueueStatsResponse, DocumentStatsResponse } from '../types';

interface KPIPanelProps {
  queueStats: QueueStatsResponse | null;
  documentStats: DocumentStatsResponse | null;
}

export function KPIPanel({ queueStats, documentStats }: KPIPanelProps) {
  // Get tag queue stats (final step = completed documents)
  const tagQueue = queueStats?.queues?.tag;

  // Calculate totals from queue stats (for active/waiting jobs across pipeline)
  const queueTotals = queueStats
    ? Object.values(queueStats.queues).reduce(
        (acc, queue) => {
          acc.active += queue.active;
          acc.waiting += queue.waiting;
          return acc;
        },
        { active: 0, waiting: 0 }
      )
    : null;

  // Get average processing time from document stats
  const avgTime = documentStats?.averageProcessingTime;
  const avgTimeFormatted = avgTime ? formatDuration(avgTime) : '--';

  // Calculate throughput based on tag queue completed (= documents fully processed)
  const completedDocs = tagQueue?.completed ?? 0;
  const throughput = completedDocs > 0 && documentStats?.runDuration
    ? calculateThroughput(completedDocs, documentStats.runDuration)
    : null;

  return (
    <Card className="bg-gray-900 ring-gray-800">
      <Title className="text-gray-100">Key Metrics</Title>

      <Grid numItems={2} className="mt-4 gap-4">
        {/* Completed Documents */}
        <KPICard
          title="Completed"
          value={tagQueue?.completed ?? 0}
          color="emerald"
          tooltip="Documents that completed all pipeline steps"
        />

        {/* Active Jobs */}
        <KPICard
          title="Active"
          value={queueTotals?.active ?? 0}
          color="blue"
          pulse={queueTotals?.active ? queueTotals.active > 0 : false}
          tooltip="Jobs currently being processed across all pipeline steps"
        />

        {/* Failed Documents */}
        <KPICard
          title="Failed"
          value={tagQueue?.failed ?? 0}
          color={tagQueue?.failed ? 'red' : 'gray'}
          tooltip="Documents that failed during processing"
        />

        {/* Avg Time */}
        <KPICard
          title="Avg Time"
          value={avgTimeFormatted}
          isText
          color="gray"
          tooltip="Average time to process a document through all pipeline steps"
        />
      </Grid>

      {/* Throughput */}
      <div className="mt-4 pt-4 border-t border-gray-800">
        <Flex justifyContent="between" alignItems="center">
          <div className="flex items-center">
            <Text className="text-gray-400">Throughput</Text>
            <Tooltip text="Average documents completed per minute, calculated from completed documents divided by run duration." />
          </div>
          <Text className="text-gray-200 font-medium">
            {throughput !== null ? `~${throughput} docs/min` : '--'}
          </Text>
        </Flex>
        {documentStats?.runDuration && (
          <Flex justifyContent="between" alignItems="center" className="mt-1">
            <Text className="text-gray-500 text-xs">Run Duration</Text>
            <Text className="text-gray-400 text-xs">{documentStats.runDuration}</Text>
          </Flex>
        )}
      </div>
    </Card>
  );
}

interface KPICardProps {
  title: string;
  value: number | string;
  color: 'emerald' | 'blue' | 'red' | 'gray';
  isText?: boolean;
  pulse?: boolean;
  tooltip?: string;
}

function KPICard({ title, value, color, isText, pulse, tooltip }: KPICardProps) {
  const colorClasses = {
    emerald: 'text-emerald-400',
    blue: 'text-blue-400',
    red: 'text-red-400',
    gray: 'text-gray-300',
  };

  const bgClasses = {
    emerald: 'bg-emerald-950/30',
    blue: 'bg-blue-950/30',
    red: 'bg-red-950/30',
    gray: 'bg-gray-800/50',
  };

  return (
    <div className={`rounded-lg p-4 ${bgClasses[color]}`}>
      <div className="flex items-center">
        <Text className="text-gray-500 text-xs uppercase tracking-wide">{title}</Text>
        {tooltip && <Tooltip text={tooltip} />}
      </div>
      <div className={`mt-1 ${pulse ? 'animate-pulse' : ''}`}>
        {isText ? (
          <Text className={`text-2xl font-bold ${colorClasses[color]}`}>
            {value}
          </Text>
        ) : (
          <Metric className={colorClasses[color]}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </Metric>
        )}
      </div>
    </div>
  );
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = seconds / 60;
  return `${minutes.toFixed(1)}m`;
}

function calculateThroughput(completed: number, runDuration: string): number | null {
  // Parse duration like "2h 15m" or "45m" or "30s"
  const hourMatch = runDuration.match(/(\d+)h/);
  const minMatch = runDuration.match(/(\d+)m/);
  const secMatch = runDuration.match(/(\d+)s/);

  let totalMinutes = 0;
  if (hourMatch) totalMinutes += parseInt(hourMatch[1]) * 60;
  if (minMatch) totalMinutes += parseInt(minMatch[1]);
  if (secMatch) totalMinutes += parseInt(secMatch[1]) / 60;

  if (totalMinutes === 0) return null;
  return Math.round(completed / totalMinutes);
}
